import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Inbox } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';

import { useReleasesForRepo } from '../api';
import { ReleaseCard } from './ReleaseCard';

const PAGE_SIZE = 20;

interface Props {
  repoName: string;
}

export function ReleaseDetail({ repoName }: Props) {
  const [offset, setOffset] = useState(0);
  const releases = useReleasesForRepo(repoName, { limit: PAGE_SIZE, offset });

  if (releases.isLoading) {
    return (
      <div className="space-y-4" data-testid="release-detail-loading">
        <Skeleton className="h-7 w-64" />
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-64 w-full" />
        ))}
      </div>
    );
  }

  if (releases.isError) {
    return (
      <div className="space-y-4">
        <Button variant="link" asChild className="px-0">
          <Link to="/releases">
            <ChevronLeft className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            All releases
          </Link>
        </Button>
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm">
          <div className="text-destructive">Couldn&apos;t load releases for this product.</div>
          <Button variant="outline" size="sm" className="mt-2" onClick={() => releases.refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const items = releases.data?.items ?? [];
  const total = releases.data?.total ?? 0;
  const displayName = items[0]?.productDisplayName ?? repoName;

  return (
    <div className="space-y-6">
      <div>
        <Button variant="link" asChild className="px-0">
          <Link to="/releases">
            <ChevronLeft className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            All releases
          </Link>
        </Button>
      </div>
      <PageHeader title={displayName} />

      {items.length === 0 ? (
        <EmptyState Icon={Inbox} title="No releases for this product" />
      ) : (
        <div className="space-y-4">
          {items.map((release) => (
            <ReleaseCard key={release.tag} release={release} expanded />
          ))}
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between border-t pt-4">
          <div className="text-sm text-muted-foreground">
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
